/**
 * Edit a lead.
 *
 * Status and follow-up are deliberately absent: both have their own flows on the
 * detail screen (a terminal status needs a reason in the same request; a
 * follow-up increments a counter this form must not touch). Because the reader
 * gates on `form.has()`, omitting those inputs leaves the stored values alone
 * rather than clearing them.
 */

import { Form, redirect, useActionData, useLoaderData, useNavigation } from 'react-router-dom'
import type { ActionFunctionArgs, LoaderFunctionArgs } from 'react-router-dom'
import { updateLeadInput, type Lead } from '@erp/contracts'
import { api, ApiError } from '../api'
import { invalid, issuesFrom, type FormResult } from '../forms'
import { LeadFields, readLeadForm } from '../components/lead-form'
import { displayName } from '../format'
import { Button, PageHeader } from '../ui'

export async function loader({ params }: LoaderFunctionArgs): Promise<Lead> {
  const id = params.leadId
  if (!id) throw new Response('Missing lead id', { status: 400 })

  const lead = await api.leads.get(id)
  // A converted lead is immutable; the service rejects the write anyway, so send
  // the user to the record rather than to a form that cannot save.
  if (lead.convertedAt !== null) throw redirect(`/crm/leads/${id}`)
  return lead
}

export async function action({ request, params }: ActionFunctionArgs): Promise<FormResult | Response> {
  const id = params.leadId
  if (!id) throw new Response('Missing lead id', { status: 400 })

  const parsed = updateLeadInput.safeParse(readLeadForm(await request.formData()))
  if (!parsed.success) return invalid(issuesFrom(parsed.error))

  try {
    await api.leads.update(id, parsed.data)
    return redirect(`/crm/leads/${id}`)
  } catch (error) {
    if (error instanceof ApiError) return invalid(error.issues, error.message)
    throw error
  }
}

export default function EditLead() {
  const lead = useLoaderData() as Lead
  const result = useActionData() as FormResult | undefined
  const navigation = useNavigation()
  const saving = navigation.state === 'submitting'

  return (
    <>
      <PageHeader title={`Edit ${displayName(lead)}`} />
      {result?.message ? <div className="ax-alert ax-alert-danger">{result.message}</div> : null}

      <Form method="post" className="ax-form">
        <LeadFields lead={lead} issues={result?.issues ?? {}} showPipeline={false} />
        <div className="ax-form-actions">
          <Button variant="solid" type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
          <a className="ax-btn" href={`/crm/leads/${lead.id}`}>
            Cancel
          </a>
        </div>
      </Form>
    </>
  )
}
